import { Measure } from 'services/model/model.service';
import { ColumnDef, createColumnHelper, Row } from '@tanstack/react-table';
import { FormattedDisplayName, IntlShape, useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import { buildDrillDownTableCoreColumns } from 'pages/trading/outlier/DrilldownCoreColumns';
import { DrillDownTableDateGmvValues } from 'pages/trading/outlier/DrillDownTableDateRangeRow';
import { MessageSentiment } from 'pages/trading/model';
import { ShareBar } from 'pages/insights-pro/common/reports/ShareBar';
import { Flag } from 'components/Flag';
import { TRADING_ROOT } from 'pages/trading/Trading';
import { DataTableContextProvider } from 'components/table/context/DataTableContextProvider';
import { DataTable } from 'components/table/DataTable';
import { useCoreDrilldownExportConfigOptions } from 'pages/trading/outlier/DrilldownExportUtil';

export interface CountryRow extends DrillDownTableDateGmvValues {
  chnCountry: string;
}

const columnHelper = createColumnHelper<CountryRow>();
const buildColumns = (headers: string[], intl: IntlShape, measure: Measure) => [
  columnHelper.accessor('chnCountry', {
    header: () => headers[0],
    cell: info => (
      <div className="flex gap-2 items-center">
        <Flag
          countryCode={info.getValue()}
          tooltip={intl.formatDisplayName(info.getValue(), { type: 'region' }) ?? ''}
        />
        <FormattedDisplayName value={info.getValue()} type="region" />
      </div>
    ),
  }),
  columnHelper.accessor(row => row.share, {
    id: 'share',
    header: () => intl.formatMessage({ id: 'trading.drilldown.column.share' }),
    cell: info => <ShareBar width={info.getValue() * 100} color="#314968" />,
  }),
  ...buildDrillDownTableCoreColumns(intl, measure),
];

export interface CategoriesTableProps {
  rows: CountryRow[];
  measure: Measure;
  sentiment?: MessageSentiment;
  detectionDate?: string;
}

export function CountriesTable({
  rows,
  measure,
  sentiment = 'NEUTRAL',
  detectionDate = undefined,
}: CategoriesTableProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const intl = useIntl();

  const onRowClick = (row: Row<unknown>) => {
    const castedRow = row as Row<CountryRow>;
    navigate(`${TRADING_ROOT}/drilldown/chnCountry/${castedRow.original.chnCountry}${location.search}`);
  };

  const columns = buildColumns(
    [intl.formatMessage({ id: 'trading.drilldown.column.country' })],
    intl,
    measure
  ) as ColumnDef<unknown>[];

  const coreDrilldownExportConfigOptions = useCoreDrilldownExportConfigOptions('countries');

  return (
    <DataTableContextProvider>
      <DataTable
        testId="trading-drilldown-countries-table"
        titleId="trading.analysis.level.header.COUNTRY"
        subTitleDecorator={header =>
          `${header}${detectionDate ? ` ${intl.formatMessage({ id: 'trading.drilldown.detection.date' })} ${detectionDate}` : ''}`
        }
        rows={rows}
        columns={columns}
        onRowClick={onRowClick}
        exportConfigOptions={coreDrilldownExportConfigOptions}
        defaultSortingState={[{ id: 'onDayComparisonGmv', desc: sentiment !== 'NEGATIVE' }]}
      />
    </DataTableContextProvider>
  );
}
